
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useToast } from "@/hooks/useToast";
import { AuthForm } from "@/components/AuthForm";
import { UserExistsDialog } from "@/components/auth/UserExistsDialog";
import { useAuth } from "@/hooks/useAuth";
import { DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/ui/logo";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast: hookToast } = useToast(); // Keep this for backward compatibility
  const [authMode, setAuthMode] = useState<"login" | "register">("login");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUserExistsDialog, setShowUserExistsDialog] = useState(false);
  const [userExistsEmail, setUserExistsEmail] = useState<string | undefined>(undefined);
  const {
    login,
    register,
    loginWithGoogle,
    registerWithGoogle,
    isAuthStatusKnown,
    user,
    loading: authLoading,
    authError,
    resetAuthState,
    isAuthActionInProgress
  } = useAuth();

  // Reset form state when changing modes
  useEffect(() => {
    resetAuthState();
  }, [authMode, resetAuthState]);

  // Check URL params for mode and handle OAuth redirects
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");
    const error = params.get("error");
    const errorDescription = params.get("error_description");

    // Handle OAuth errors
    if (error) {
      console.error("OAuth error:", error, errorDescription);
      const errorMessage = errorDescription || 'Erro na autenticação com Google';
      toast.error(errorMessage);
      // Clean URL parameters
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // Handle OAuth success (access_token indicates successful OAuth)
    const accessToken = params.get("access_token");
    if (accessToken && !user) {
      // OAuth callback will be handled by Supabase auth state listener
      console.log("OAuth callback detected");
      setAuthMode("login");
      return;
    }

    if (mode === "register") {
      setAuthMode("register");
    } else if (mode === "login") {
      setAuthMode("login");
    }
  }, [location.search, user]);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthStatusKnown && user && !authLoading) {
      const redirectTo = location.state?.from || '/dashboard';
      console.log("User already logged in, redirecting to:", redirectTo);
      navigate(redirectTo, { replace: true });
    }
  }, [user, isAuthStatusKnown, authLoading, navigate, location.state]);

  // Handle form submission with debounce to prevent multiple submissions
  const handleSubmit = async (data: any) => {
    if (isSubmitting || isAuthActionInProgress()) {
      console.log("Submission already in progress, ignoring request");
      return;
    }

    try {
      setIsSubmitting(true);
      console.log("Form submitted:", { mode: authMode, data });

      if (authMode === "login") {
        await login(data.email, data.password);
        toast.success('Login realizado com sucesso!');
        navigate('/dashboard', { replace: true });
      } else {
        const { email, password, fullName } = data;
        console.log("Registering with:", { email, password, fullName });

        try {
          const result = await register(email, password, fullName);

          if (result?.emailConfirmationRequired) {
            // If email confirmation required, stay on login page
            toast.success('Conta criada com sucesso! Verifique seu email para confirmar o cadastro.');
            setAuthMode("login");
          } else {
            // If auto-signed in (no email confirmation required)
            toast.success('Conta criada com sucesso!');
            navigate('/dashboard', { replace: true });
          }
        } catch (error: any) {
          // Check if error is due to user already exists
          if (error.message === 'user_already_exists' || error.message.includes('já está cadastrado')) {
            setUserExistsEmail(email);
            setShowUserExistsDialog(true);
          } else {
            throw error; // Re-throw other errors to be handled by the outer catch block
          }
        }
      }
    } catch (error: any) {
      console.error("Auth error:", error);
      const errorMessage = error.message || "Ocorreu um erro durante a autenticação.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // If still checking auth status, show loading
  if (!isAuthStatusKnown || (authLoading && !authError)) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-background">
        <div className="animate-pulse mb-8">
          <Logo variant="full" size="lg" />
        </div>
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Verificando autenticação...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 font-primary">
      <div className="hidden md:flex relative overflow-hidden bg-gradient-to-br from-blue-dark-900 via-blue-dark-800 to-blue-medium-50">
        {/* Background Texture Overlay */}
        <div className="absolute inset-0 opacity-20">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='7' cy='7' r='7'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
            }}
          />
        </div>

        {/* Main Content Container */}
        <div className="relative z-10 flex flex-col justify-center w-full p-8 lg:p-12">
          {/* Logo Section */}
          <Link to="/" className="block mb-8 group">
            <div className="filter brightness-0 invert transition-transform duration-300 group-hover:scale-105">
              <Logo variant="full" size="lg" />
            </div>
          </Link>

          {/* Image Gallery Section - Framer Style Composition */}
          <div className="relative mb-12" style={{ height: '400px', perspective: '1200px' }}>
            <div className="relative w-full h-full">
              {/* Image 1 - Left Side (organizacao) */}
              <div
                className="absolute"
                style={{
                  top: '30px',
                  left: '60px',
                  width: '298px',
                  height: '210px',
                  borderRadius: '10px',
                  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 4px 20px 0px',
                  transform: 'perspective(1200px) translateZ(0px)',
                  opacity: 1
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    borderRadius: 'inherit',
                    top: '0',
                    right: '0',
                    bottom: '0',
                    left: '0'
                  }}
                >
                  <img
                    src="/images/organizacao.avif"
                    alt="Organização Financeira"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      borderRadius: 'inherit',
                      objectPosition: 'center',
                      objectFit: 'cover'
                    }}
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Image 2 - Right Side (planejamento) */}
              <div
                className="absolute"
                style={{
                  top: '0px',
                  right: '20px',
                  width: '324px',
                  height: '224px',
                  borderRadius: '10px',
                  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 4px 20px 0px',
                  transform: 'perspective(1200px) translateZ(0px)',
                  opacity: 1
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    borderRadius: 'inherit',
                    top: '0',
                    right: '0',
                    bottom: '0',
                    left: '0'
                  }}
                >
                  <img
                    src="/images/planejamento.avif"
                    alt="Planejamento Financeira"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      borderRadius: 'inherit',
                      objectPosition: 'center',
                      objectFit: 'cover'
                    }}
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Image 3 - Bottom Center (analise) */}
              <div
                className="absolute"
                style={{
                  bottom: '20px',
                  left: '50%',
                  transform: 'perspective(1200px) translateX(-50%) translateZ(0px)',
                  width: '314px',
                  height: '221px',
                  borderRadius: '10px',
                  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 4px 20px 0px',
                  opacity: 1
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    borderRadius: 'inherit',
                    top: '0',
                    right: '0',
                    bottom: '0',
                    left: '0'
                  }}
                >
                  <img
                    src="/images/analise.avif"
                    alt="Análise Financeira"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      borderRadius: 'inherit',
                      objectPosition: 'center',
                      objectFit: 'cover'
                    }}
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Labels overlay */}
              <div
                className="absolute top-1/2 left-8 transform -translate-y-1/2 bg-mustard-500 rounded-lg shadow-lg px-4 py-2"
                style={{
                  width: 'fit-content',
                  backgroundColor: 'rgb(0, 119, 197)',
                  borderRadius: '10px',
                  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 4px 20px 0px'
                }}
              >
                <p style={{
                  fontFamily: '"Ondo Regular", sans-serif',
                  fontSize: '15px',
                  letterSpacing: '-0.02em',
                  color: 'rgb(255, 255, 255)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  # organização
                </p>
              </div>

              <div
                className="absolute top-1/2 right-8 transform -translate-y-1/2 bg-mustard-500 rounded-lg shadow-lg px-4 py-2"
                style={{
                  width: 'fit-content',
                  backgroundColor: 'rgb(0, 119, 197)',
                  borderRadius: '10px',
                  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 4px 20px 0px'
                }}
              >
                <p style={{
                  fontFamily: '"Ondo Regular", sans-serif',
                  fontSize: '15px',
                  letterSpacing: '-0.02em',
                  color: 'rgb(255, 255, 255)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  # planejamento
                </p>
              </div>

              <div
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-mustard-500 rounded-lg shadow-lg px-4 py-2"
                style={{
                  width: 'fit-content',
                  backgroundColor: 'rgb(0, 119, 197)',
                  borderRadius: '10px',
                  boxShadow: 'rgba(0, 0, 0, 0.12) 0px 4px 20px 0px'
                }}
              >
                <p style={{
                  fontFamily: '"Ondo Regular", sans-serif',
                  fontSize: '15px',
                  letterSpacing: '-0.02em',
                  color: 'rgb(255, 255, 255)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  # análise
                </p>
              </div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="max-w-lg">
            <h1
              className="text-primary-foreground mb-6 leading-tight"
              style={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: 'clamp(1.75rem, 4vw, 2.25rem)',
                fontWeight: 700,
                letterSpacing: '-0.03em',
              }}
            >
              Controle financeiro com <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-medium-400 to-blue-dark-600">inteligência</span>
            </h1>

            <p
              className="text-muted-foreground mb-8 leading-relaxed"
              style={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                fontWeight: 400,
                letterSpacing: '-0.01em',
                lineHeight: '1.6'
              }}
            >
              Transforme sua relação com o dinheiro através de organização, planejamento estratégico e análises precisas que impulsionam seu crescimento financeiro.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: "📊", text: "Dashboards em tempo real" },
                { icon: "🎯", text: "Metas personalizadas" },
                { icon: "📈", text: "Relatórios detalhados" },
                { icon: "🔒", text: "Segurança máxima" }
              ].map((feature, index) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-card/50 backdrop-blur-sm rounded-full border border-border/50 text-foreground text-sm transition-all duration-300 hover:bg-card/70 hover:scale-105"
                  style={{
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  <span className="text-base">{feature.icon}</span>
                  <span style={{ fontFamily: '"Montserrat", sans-serif', fontWeight: 500 }}>
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Accent */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-muted-foreground text-sm" style={{ fontFamily: '"Montserrat", sans-serif' }}>
              Junte-se a milhares que já transformaram suas finanças
            </p>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-8 w-32 h-32 bg-blue-dark-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-8 w-40 h-40 bg-blue-medium-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">
              {authMode === "login" ? "Bem-vindo de volta!" : "Crie sua conta"}
            </h2>
            <p className="text-muted-foreground mt-2">
              {authMode === "login"
                ? "Faça login para acessar sua conta"
                : "Preencha seus dados para começar"}
            </p>
          </div>

          <AuthForm
            mode={authMode}
            onSubmit={handleSubmit}
            onToggleMode={() => {
              setAuthMode(authMode === "login" ? "register" : "login");
              // Update URL without navigating
              const newMode = authMode === "login" ? "register" : "login";
              const newUrl = `${window.location.pathname}?mode=${newMode}`;
              window.history.replaceState({}, "", newUrl);
            }}
            onGoogleSignIn={async () => {
              try {
                await loginWithGoogle();
                // OAuth will redirect, so we don't need to navigate here
              } catch (error) {
                console.error("Google login error:", error);
              }
            }}
            onGoogleSignUp={async () => {
              try {
                await registerWithGoogle();
                // OAuth will redirect, so we don't need to navigate here
              } catch (error) {
                console.error("Google signup error:", error);
              }
            }}
            isLoading={isSubmitting || isAuthActionInProgress()}
          />

          {isSubmitting && (
            <div className="flex justify-center mt-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {authError && (
            <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm mt-4">
              {authError}
            </div>
          )}
        </div>
      </div>

      <UserExistsDialog
        open={showUserExistsDialog}
        onOpenChange={setShowUserExistsDialog}
        onSwitchToLogin={() => setAuthMode("login")}
        email={userExistsEmail}
      />
    </div>
  );
}
